import React, { useState } from "react";
import {
  Box,
  Button,
  TextField,
  Typography,
  Paper,
  Container,
  Alert,
  Grid,
  Link,
  InputAdornment,
  IconButton,
} from "@mui/material";
import {
  Visibility,
  VisibilityOff,
  Email as EmailIcon,
  Badge as BadgeIcon,
} from "@mui/icons-material";
import { useNavigate, Link as RouterLink } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import NetworkTroubleshooter from "../components/NetworkTroubleshooter";
import ConnectionDiagnostics from "../components/ConnectionDiagnostics";

// Add diagnostic function for testing
const testApiConnection = async () => {
  const serverUrl = process.env.REACT_APP_SERVER_URL || "http://localhost:5000";
  console.log("🧪 Direct API Test from Login Page:");
  console.log("Server URL:", serverUrl);

  try {
    const response = await fetch(`${serverUrl}/api/v1/auth/university/test123`);
    const data = await response.json();
    console.log("✅ Direct fetch result:", { status: response.status, data });
    return { success: true, data };
  } catch (error) {
    console.error("❌ Direct fetch failed:", error);
    return { success: false, error: error.message };
  }
};

const Login = () => {
  const navigate = useNavigate();
  const { login, error: authError } = useAuth();
  const [formData, setFormData] = useState({
    universityId: "",
    email: "",
    password: "",
  });
  const [showPassword, setShowPassword] = useState(false);
  const [formError, setFormError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isEmailLogin, setIsEmailLogin] = useState(false);
  const [showNetworkTroubleshooting, setShowNetworkTroubleshooting] =
    useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
    // Clear error when user starts typing
    if (formError) setFormError("");
    setShowNetworkTroubleshooting(false);
  };

  const toggleLoginMethod = () => {
    setIsEmailLogin(!isEmailLogin);
    setFormError("");
    // Clear the appropriate field when toggling
    if (isEmailLogin) {
      setFormData({ ...formData, email: "" });
    } else {
      setFormData({ ...formData, universityId: "" });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault(); // Prevent default form submission
    setFormError("");
    setIsLoading(true);

    try {
      // Validate fields based on login method
      if (isEmailLogin) {
        if (!formData.email || !formData.password) {
          setFormError("Please enter both email and password");
          setIsLoading(false);
          return;
        }

        console.log("Email login form submitted with:", {
          email: formData.email,
          password: "***",
        });

        // Email-based login
        await login({
          email: formData.email,
          password: formData.password,
        });

        console.log("Email login successful");

        // Handle redirect based on result
        navigate("/dashboard", { replace: true });
      } else {
        // University ID-based login
        if (!formData.universityId || !formData.password) {
          setFormError("Please enter both University ID and password");
          setIsLoading(false);
          return;
        }

        console.log("UniversityID login form submitted with:", {
          universityId: formData.universityId,
          password: "***",
        });

        // University ID-based login returns object with `requirePasswordChange` flag
        const result = await login({
          universityId: formData.universityId,
          password: formData.password,
        });
        console.log("UniversityID login successful, result:", result);
        // If first-time login, redirect to change password
        if (result?.requirePasswordChange) {
          navigate("/change-password", { replace: true });
        } else {
          navigate("/dashboard", { replace: true });
        }
      }
    } catch (err) {
      console.error("Login form error:", err);
      const errorMessage =
        err.response?.data?.message ||
        authError ||
        err.message ||
        "Login failed. Please check your credentials.";

      setFormError(errorMessage);

      // Show network troubleshooting for network-related errors
      if (errorMessage.toLowerCase().includes("network")) {
        setShowNetworkTroubleshooting(true);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleRetryLogin = () => {
    setFormError("");
    setShowNetworkTroubleshooting(false);
    // Trigger form submission again
    handleSubmit(new Event("submit"));
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  return (
    <Container maxWidth="sm">
      <Box
        sx={{
          marginTop: 8,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
        }}
      >
        <Paper
          elevation={3}
          sx={{
            p: 4,
            width: "100%",
            borderRadius: 2,
            background: "linear-gradient(to bottom right, #ffffff, #f5f5f5)",
          }}
        >
          <Typography component="h1" variant="h4" align="center" gutterBottom>
            SEED Organizr
          </Typography>
          <Typography
            component="h2"
            variant="h5"
            align="center"
            sx={{ mb: 3, color: "text.secondary" }}
          >
            Log In
          </Typography>

          {(formError || authError) && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {formError || authError}
              {(formError || authError)?.includes("Network") && (
                <Box sx={{ mt: 1, fontSize: "0.875rem" }}>
                  💡 <strong>Tip:</strong> If you just logged out, please wait a
                  moment and try again. This helps ensure a clean authentication
                  state.
                </Box>
              )}
            </Alert>
          )}

          {/* Network Troubleshooting Component */}
          <NetworkTroubleshooter
            show={showNetworkTroubleshooting}
            onRetry={handleRetryLogin}
            errorMessage={formError || authError}
          />

          {/* Connection Diagnostics Component */}
          {showNetworkTroubleshooting && <ConnectionDiagnostics />}

          {/* Helpful info for new users */}
          <Alert severity="info" sx={{ mb: 2 }}>
            🔐 <strong>Login Methods:</strong> Use your University ID or Email
            address with your password.
            {!isEmailLogin
              ? " Toggle to email login if needed."
              : " Toggle to University ID if needed."}
          </Alert>

          <Box component="form" onSubmit={handleSubmit} noValidate>
            {isEmailLogin ? (
              <TextField
                margin="normal"
                required
                fullWidth
                id="email"
                label="Email Address"
                name="email"
                autoComplete="email"
                autoFocus
                value={formData.email}
                onChange={handleChange}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <EmailIcon />
                    </InputAdornment>
                  ),
                }}
              />
            ) : (
              <TextField
                margin="normal"
                required
                fullWidth
                id="universityId"
                label="University ID"
                name="universityId"
                autoComplete="username"
                autoFocus
                value={formData.universityId}
                onChange={handleChange}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <BadgeIcon />
                    </InputAdornment>
                  ),
                }}
              />
            )}
            <TextField
              margin="normal"
              required
              fullWidth
              name="password"
              label="Password"
              type={showPassword ? "text" : "password"}
              id="password"
              autoComplete="current-password"
              value={formData.password}
              onChange={handleChange}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={togglePasswordVisibility}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <Button
              type="submit"
              fullWidth
              variant="contained"
              color="primary"
              size="large"
              disabled={isLoading}
              sx={{ mt: 3, mb: 2 }}
            >
              {isLoading ? "Logging in..." : "Sign In"}
            </Button>

            {/* Debug Test Button - only show when there are connection issues */}
            {showNetworkTroubleshooting && (
              <Button
                fullWidth
                variant="outlined"
                color="secondary"
                size="small"
                onClick={testApiConnection}
                sx={{ mb: 2 }}
              >
                🧪 Test API Connection (Debug)
              </Button>
            )}

            <Grid
              container
              justifyContent="space-between"
              alignItems="center"
              sx={{ mb: 2 }}
            >
              <Grid item>
                <Link
                  component="button"
                  variant="body2"
                  onClick={toggleLoginMethod}
                  type="button"
                >
                  Login with {isEmailLogin ? "University ID" : "Email"} instead
                </Link>
              </Grid>
              <Grid item>
                <Link
                  component={RouterLink}
                  to="/forgot-password"
                  variant="body2"
                >
                  Forgot password?
                </Link>
              </Grid>
            </Grid>

            <Grid container>
              <Grid item>
                <Link component={RouterLink} to="/register" variant="body2">
                  {"Don't have an account? Sign Up"}
                </Link>
              </Grid>
            </Grid>
          </Box>
        </Paper>
      </Box>
    </Container>
  );
};

export default Login;
